<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    @reload="clearForm"
  >
    <template #top-right-slot>
      <DsButton
        v-tooltip="infoLabel"
        color="tertiary"
        name="questions"
        :icon="true"
        @click="openDialog"
      />
    </template>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions
          class="close-button"
        >
          <DsButton
            v-tooltip="closeLabel"
            #top-right
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="modal-style">
          <h3>{{ this.$t('components.FileImporter.faq.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq.description') }}</p>

          <h3>{{ this.$t('components.FileImporter.faq2.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq2.description') }}</p>

          <h3>{{ this.$t('components.FileImporter.faq3.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq3.description') }}</p>

          <h3>{{ this.$t('components.FileImporter.faq4.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq4.description') }}</p>

          <h3>{{ this.$t('components.FileImporter.faq5.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq5.description') }}</p>

          <h3>{{ this.$t('components.FileImporter.faq6.title') }}</h3>
          <p>{{ this.$t('components.FileImporter.faq6.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <StLoader :loading="isSending" />
    <v-form
      ref="form"
      v-model="valid"
    >
      <v-row>
        <v-col>
          <template>
            <v-file-input
              v-model="file"
              accept="application/pdf"
              :disabled="isSending"
              :rules="rules.isValue"
              :placeholder="$t('components.FileImporter.label.insertFile')"
              :label="$t('components.FileImporter.label.file')"
              prepend-icon="mdi-paperclip"
              required
            >
              <template v-slot:selection="{ text }">
                <v-chip
                  small
                  label
                  color="primary"
                >
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </template>
          <v-text-field
            v-model="link"
            :disabled="isSending"
            :rules="rules.isValue"
            prepend-icon="mdi-link-variant"
            dense
            required
            :label="$t('components.FileImporter.label.link')"
          />
          <template>
            <v-dialog
              ref="dialog"
              v-model="modal"
              :return-value.sync="date"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedDate"
                  class="date"
                  :label="$t('components.FileImporter.label.date')"
                  :disabled="isSending"
                  :rules="rules.isValue"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="date"
                scrollable
                required
                :max="today"
              >
                <v-spacer />
                <DsButton
                  class="date-buttons"
                  color="secondary"
                  @click="modal = false"
                >
                  {{ $t('components.FileImporter.label.cancel') }}
                </DsButton>
                <DsButton
                  class="date-buttons"
                  color="primary"
                  @click="$refs.dialog.save(date)"
                >
                  {{ $t('components.FileImporter.label.ok') }}
                </DsButton>
              </v-date-picker>
            </v-dialog>
          </template>
          <v-checkbox
            v-model="hiddenData"
            class="maskedInfo"
            :label="$t('components.FileImporter.label.checkbox')"
          />
        </v-col>
      </v-row>
    </v-form>
    <v-row
      no-gutters
      justify="end"
    >
      <DsButton
        :disabled="isSending"
        :loading="isSending"
        color="primary"
        rounded
        large
        @click="postData"
      >
        {{ $t('components.FileImporter.label.send') }}
      </DsButton>
      <DsButton
        :disabled="isSending"
        color="secondary"
        class="ml-3"
        rounded
        large
        @click="clearForm"
      >
        {{ $t('components.FileImporter.label.clear') }}
      </DsButton>
    </v-row>
  </CardWithTitle>
</template>

<script>
import { mapState } from 'vuex';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import { DsButton } from '@ds/components';
import StLoader from '@stilingue/st-loader';
import ImporterService from '@/services/ImporterService';
import { setDate, setDateToUTC } from '@/utils/date';

export default {
  name: 'FileImporter',
  components: {
    CardWithTitle,
    DsButton,
    StLoader,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      file: null,
      dialog: false,
      formattedDate: null,
      url: null,
      requestStatus: 'success',
      hiddenData: false,
      infoLabel: this.$t('components.FileImporter.infoLabel'),
      closeLabel: this.$t('components.FileImporter.closeLabel'),
      isSending: false,
      valid: true,
      link: null,
      date: null,
      today: new Date().toISOString().substring(0, 10),
      modal: false,
      rules: {
        isValue: [v => Boolean(v) || this.$t('components.FileImporter.label.requiredValue')],
      },
      token: null,
    };
  },

  computed: {
    ...mapState('user', [
      'user',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
      'apiToken',
    ]),
  },

  watch: {
    date() {
      if (this.date) {
        this.formattedDate = this.formatDate(this.date);
      }
    },
  },

  methods: {
    formatDate(date) {
      const selectedDate = setDateToUTC(date);
      const formattedDate = `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`;
      return formattedDate;
    },
    openDialog() {
      this.dialog = true;
    },
    createFormData() {
      const selectedDate = setDate(this.date);

      const formData = new FormData();
      formData.append('userId', this.user.id);
      formData.append('userName', this.user.name);
      formData.append('files', this.file);
      formData.append('storageLink', this.link);
      formData.append('date', selectedDate);
      formData.append('isHidden', this.hiddenData);

      return formData;
    },
    createQueryStrings() {
      const options = new URLSearchParams();
      options.append('accountId', this.accountId);
      options.append('universeId', this.universeId);
      options.append('tokenApi', this.apiToken);
      return options;
    },
    clearForm() {
      this.$refs.form.reset();
      this.formdata = null;
      this.formattedDate = null;
      this.date = null;
      this.hiddenData = false;
    },
    async postData() {
      this.isSending = true;
      await ImporterService.importer(this.createFormData(), this.createQueryStrings())
        .then(() => {
          this.$noty.success(this.$t('components.FileImporter.upload.success'));
          this.clearForm();
        })
        .catch(error => {
          this.errorHandler(error);
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    errorHandler(e) {
      console.error(e);
      const httpStatusDuplicatedFile = { NOT_MODIFIED: '304' };
      const httpStatusError = e.toString().slice(-3);
      if (httpStatusError === httpStatusDuplicatedFile.NOT_MODIFIED) {
        this.$noty.warning(this.$t('components.FileImporter.upload.duplicated'));
      } else {
        this.$noty.error(this.$t('components.FileImporter.upload.error'));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }

  .date-buttons {
    width: 224px;
    justify-self: center;
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }
  }

  ::v-deep {
    .v-input {
      display: flex;
    }
  }
</style>
